import React from 'react';
import { Badge } from '../ui/Badge';
import {Eye, Globe, Shield, AlertTriangle, AlertOctagon, AlertCircle } from 'lucide-react';
import { AssetActions } from './AssetActions';
import { TechnologyStack } from './TechnologyStack';
import * as Tooltip from '@radix-ui/react-tooltip';
import { TablePagination } from '../ui/TablePagination';
import { Pagination } from '../ui/Pagination';
import { Link, useLocation } from 'react-router-dom';
import { Subdomain, SubdomainToJSONTyped } from 'api-client';

interface TlsCertificate {
  sni?: string;
  host?: string;
  port?: string;
  cipher?: string;
  issuer_cn?: string;
  not_after?: string;
  not_before?: string;
  subject_cn?: string;
  tls_version?: string;
  probe_status?: boolean;
}

// interface Subdomain {
//   id: string;
//   subdomainName: string;
//   parentDomain: {
//     id: string;
//     name: string;
//   };
//   isActive: boolean;
//   riskScore: string;
//   updatedAt: string;
//   tags: string[];
//   technologies: Array<{
//     name: string;
//     version: string;
//     icon: string;
//     color: string;
//   }>;
//   dnsRecords?: any;
//   tlsCertificate?: TlsCertificate | null;
//   createdAt: string;
//   ipAddresses: string[];
// }

interface PaginationInfo {
  count: number;
  next: string | null;
  previous: string | null;
}

interface SubdomainsTableProps {
  subdomains: any[];
  currentPage: number;
  onPageChange: (page: number) => void;
  totalSubdomains: number;
  itemsPerPage: number;
  paginationInfo: PaginationInfo;
}

export const SubdomainsTable: React.FC<SubdomainsTableProps> = ({
  subdomains,
  currentPage,
  onPageChange,
  totalSubdomains,
  itemsPerPage,
  paginationInfo,
}) => {

  const getRiskColor = (score: string) => {
    switch (score) {
      case "A+": return 'text-emerald-500 bg-emerald-500/10 border-emerald-500/20';
      case "A": return 'text-green-500 bg-green-500/10 border-green-500/20';
      case "B": return 'text-yellow-500 bg-yellow-500/10 border-yellow-500/20';
      case "C": return 'text-orange-500 bg-orange-500/10 border-orange-500/20';
      case "F": return 'text-red-500 bg-red-500/10 border-red-500/20';
      default: return 'text-emerald-500 bg-emerald-500/10 border-emerald-500/20';
    }
  };
  
  const getVulnerabilityIcon = (count: number, type: 'critical' | 'high' | 'medium' | 'low') => {
  if (count === 0) return null;

  const icons = {
    critical: { icon: AlertOctagon, color: 'text-red-500' },
    high: { icon: AlertTriangle, color: 'text-orange-500' },
    medium: { icon: AlertCircle, color: 'text-yellow-500' },
    low: { icon: Shield, color: 'text-emerald-500' }
  };

  const { icon: Icon, color } = icons[type];
  
  return (
    <Tooltip.Provider>
      <Tooltip.Root>
        <Tooltip.Trigger asChild>
          <div className={`flex items-center space-x-1 ${color}`}>
            <Icon className="h-4 w-4" />
            <span className="text-xs font-medium">{count}</span>
          </div>
        </Tooltip.Trigger>
        <Tooltip.Portal>
          <Tooltip.Content
            className="px-3 py-1.5 rounded-lg bg-[#1A1F2E] border border-gray-800 text-xs text-white shadow-xl"
            sideOffset={5}
          >
            {count} {type} vulnerabilities
            <Tooltip.Arrow className="fill-[#1A1F2E]" />
          </Tooltip.Content>
        </Tooltip.Portal>
      </Tooltip.Root>
    </Tooltip.Provider>
  );
};
  const startIndex = (currentPage - 1) * itemsPerPage + 1;
  const endIndex = Math.min(currentPage * itemsPerPage, totalSubdomains);

  return (
    <div className="bg-[#0F1629] rounded-xl overflow-hidden border border-gray-800/50 flex flex-col">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-800">
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Subdomain</th>
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Vulnerabilities</th>
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Technologies</th>
              {/* <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Status</th> */}
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Risk Score</th>
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">DNS & TLS</th>
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Changes Detected</th>
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-800">
            {subdomains.map((subdomain) => (
              <tr key={subdomain.id} className="group hover:bg-gray-800/30">
                <td className="px-6 py-4">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 rounded-lg bg-gray-800/30 group-hover:bg-gray-800/50 transition-colors">
                      <Globe className="h-5 w-5 text-gray-400" />
                    </div>
                    <div>
                    <Link to={`/subdomains/${subdomain.id}`} className="text-sm font-medium text-white">{subdomain.subdomainName}</Link>
                      <div className="flex items-center space-x-2 mt-1 flex-wrap">
                        {subdomain.tags?.map((tag: any, index: number) => (
                          <span
                            key={index}
                            className="px-2 py-0.5 text-xs rounded-full bg-gray-800/50 text-gray-400 hover:bg-gray-800 hover:text-gray-300 transition-colors cursor-pointer"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4">
                  {subdomain?.vulnerabilities && 
                   (subdomain.vulnerabilities?.critical > 0 || 
                    subdomain.vulnerabilities?.high > 0 || 
                    subdomain.vulnerabilities?.medium > 0 || 
                    subdomain.vulnerabilities?.low > 0) ? (
                    <div className="flex items-center space-x-2">
                      {getVulnerabilityIcon(subdomain?.vulnerabilities?.critical, 'critical')}
                      {getVulnerabilityIcon(subdomain?.vulnerabilities?.high, 'high')}
                      {getVulnerabilityIcon(subdomain?.vulnerabilities?.medium, 'medium')}
                      {getVulnerabilityIcon(subdomain?.vulnerabilities?.low, 'low')}
                    </div>
                  ) : (
                    <div className="flex items-center">
                      <span className="px-2 py-1 text-xs rounded-md bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                        No vulnerabilities
                      </span>
                    </div>
                  )}
                </td>
                <td className="px-6 py-4">
                  {subdomain.technologies && Array.isArray(subdomain.technologies) && subdomain.technologies.length > 0 || 
                   (typeof subdomain.technologies === 'string' && subdomain.technologies) ? (
                    <TechnologyStack
                      technologies={
                        subdomain.technologies 
                          ? (typeof subdomain.technologies === 'string' 
                              ? (
                                  // Try to parse as JSON if it's a string
                                  (() => {
                                    try {
                                      const parsed = JSON.parse(subdomain.technologies);
                                      return Array.isArray(parsed) ? parsed : [];
                                    } catch (e) {
                                      // If parsing fails, return empty array
                                      return [];
                                    }
                                  })()
                                ) 
                              : subdomain.technologies
                            )
                          : []
                      }
                      maxVisible={3}
                    />
                  ) : (
                    <div className="flex items-center">
                      <span className="px-2 py-1 text-xs rounded-md bg-gray-800/50 text-gray-400 border border-gray-700/30">
                        No technologies detected
                      </span>
                    </div>
                  )}
                </td>
                {/* <td className="px-6 py-4">
                  <Badge
                    className={subdomain.isActive ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' : 'bg-red-500/10 text-red-400 border-red-500/20'}
                  >
                    {subdomain.isActive ? 'Active' : 'Inactive'}
                  </Badge>
                </td> */}
                <td className="px-6 py-4">
                  <Badge className={getRiskColor(subdomain.riskScore?.toUpperCase() || 'Unknown')}>
                    {subdomain.riskScore?.toUpperCase() || 'Unknown'}
                  </Badge>
                </td>
                <td className="px-6 py-4">
                  <div className="flex flex-col space-y-2 w-max">
                    {subdomain.tlsCertificate && (
                      <Badge
                        className={`${
                          typeof subdomain.tlsCertificate === 'object' && 
                          subdomain.tlsCertificate?.probe_status ? 
                            'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' : 
                            'bg-red-500/10 text-red-400 border-red-500/20'
                        }`}
                      >
                        <div className="flex items-center space-x-1">
                          <Shield className="h-3 w-3" />
                          <span>
                            {typeof subdomain.tlsCertificate === 'object' && 
                             subdomain.tlsCertificate?.probe_status ? 'TLS Valid' : 'TLS Invalid'}
                          </span>
                        </div>
                      </Badge>
                    )}
                    {subdomain.dnsRecords && (
                      <Badge className="bg-blue-500/10 text-blue-400 border-blue-500/20">
                        <div className="flex items-center space-x-1">
                          <span>
                            DNS Records: {
                              typeof subdomain.dnsRecords === 'object' && subdomain.dnsRecords !== null
                                ? Object.keys(subdomain.dnsRecords).length
                                : (typeof subdomain.dnsRecords === 'string'
                                  ? (() => {
                                      try {
                                        const parsed = JSON.parse(subdomain.dnsRecords);
                                        return typeof parsed === 'object' && parsed !== null
                                          ? Object.keys(parsed).length
                                          : 0;
                                      } catch (e) {
                                        return 0;
                                      }
                                    })()
                                  : 0)
                            }
                          </span>
                        </div>
                      </Badge>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4">
                  <Tooltip.Provider>
                    <Tooltip.Root>
                      <Tooltip.Trigger asChild>
                        <div className="text-sm text-gray-300 cursor-help flex flex-col w-max">
                          {subdomain.updatedAt?.split(",").map((item: string) => (
                            <span key={item}>{item}</span>
                          ))}
                        </div>
                      </Tooltip.Trigger>
                      <Tooltip.Portal>
                        <Tooltip.Content
                          className="px-3 py-1.5 rounded-lg bg-[#1A1F2E] border border-gray-800 text-xs text-white shadow-xl"
                          sideOffset={5}
                        >
                          <div className="space-y-1">
                            <div>Changes Detected: {subdomain.updatedAt}</div>
                            <div>Created: {subdomain.createdAt}</div>
                            {subdomain?.ipAddresses?.length > 0 && (
                              <div>IP Addresses: {subdomain.ipAddresses.join(', ')}</div>
                            )}
                          </div>
                          <Tooltip.Arrow className="fill-[#1A1F2E]" />
                        </Tooltip.Content>
                      </Tooltip.Portal>
                    </Tooltip.Root>
                  </Tooltip.Provider>
                </td>
                <td className="px-6 py-4">
  <div className="flex items-center gap-2">
    <Link
      to={`/subdomains/${subdomain.id}`}
      className="bg-[#1A1F2E] text-white"
    >
      <Eye className="h-4 w-4" />
    </Link>
    <AssetActions assetId={subdomain.id} />
  </div>
</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <TablePagination
        currentPage={currentPage}
        onPageChange={onPageChange}
        totalItems={totalSubdomains}
        itemsPerPage={itemsPerPage}
        paginationInfo={paginationInfo}
        itemName="subdomains"
      />
    </div>
  );
};
